import { Location } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { filter, finalize, map, startWith, take, takeUntil } from 'rxjs';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { currencyNarrowSymbol } from '@core/services/display-currency/convert-display-currency.util';
import { DisplayCurrencyService } from '@core/services/display-currency/display-currency.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { KioskCurrencyPickerModalComponent } from '@shared/modals/kiosk-currency-picker-modal/kiosk-currency-picker-modal.component';
import {
  ContentBuilderColorValue,
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { isLinkValueComplete, type LinkValue } from '@front-platform/content-builder';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { ResetStateService } from '@core/services/reset-state.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { ShopsService } from '@core/services/shops/shops.service';

import {
  KIOSK_HOME_LOADER_DELAY_MS,
  kioskCartShopId,
  kioskShopHomePath,
  resolveKioskHomeShop,
} from './kiosk-header-home';
import { readAngularNavigationId } from './kiosk-header-navigation';

@Component({
  selector: 'app-kiosk-header-atom',
  templateUrl: './kiosk-header-atom.component.html',
  standalone: false,
})
export class KioskHeaderAtomComponent extends DestroyerBase {
  readonly logoImageUrl = input<string | IContentBuilderFieldImage>();
  readonly logoUrl = input<string | LinkValue>();
  readonly backgroundColor = input<IContentBuilderFieldColor>();
  readonly backgroundImage = input<IContentBuilderFieldImage>();
  readonly backgroundUrl = input<string | LinkValue>();
  readonly headerLandingText = input<string>();
  readonly displayHomeButton = input<boolean>(false);
  readonly displayBackButton = input<boolean>(false);
  readonly backButtonIconColor = input<ContentBuilderColorValue>();
  readonly displayLeavingSessionButton = input<boolean>(false);
  readonly displayAllergenFilters = input(false);
  readonly allergenFilterCount = input(0);
  readonly openAllergenFilters = output<void>();
  readonly buttonHomeText = input<string>();
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();
  readonly displayLogo = input(false);
  readonly size = input<'L' | 'M' | 'S'>('S');
  readonly logoWidth = input<'small' | 'medium' | 'large'>('medium');

  /** Nucleo is 1em of the parent; bind the glyph to logo_width (Petite / Moyenne / Grande). */
  protected readonly leaveSessionIconSize = computed(() => {
    switch (this.logoWidth()) {
      case 'small':
        return '1.75rem';
      case 'large':
        return '3rem';
      default:
        return '2.25rem';
    }
  });

  protected readonly isLeaving = signal(false);
  protected readonly allergenButtonBackground = computed(() => {
    const css = fromContentBuilderColor(this.buttonBackgroundColor());
    return css || null;
  });
  protected readonly allergenButtonText = computed(() => {
    const css = fromContentBuilderColor(this.buttonTextColor());
    return css || null;
  });
  /** Visible only after the shop lookup has already lasted 300ms. */
  protected readonly showHomeLoader = signal(false);
  private homeLoaderTimer: ReturnType<typeof setTimeout> | undefined;
  private homeNavigationPending = false;

  protected readonly logoSrc = computed(() => {
    const logo = this.logoImageUrl();
    if (!logo) {
      return undefined;
    }
    if (typeof logo === 'string') {
      return logo;
    }
    return logo.url;
  });

  protected readonly displayCurrency = inject(DisplayCurrencyService);

  private readonly shopSelector = inject(ShopStoreSelector);
  private readonly cartSelector = inject(CartStoreSelector);
  private readonly shopsService = inject(ShopsService);
  private readonly routerHelper = inject(RouterHelperService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly navigationId = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => readAngularNavigationId(this.location.getState())),
      startWith(readAngularNavigationId(this.location.getState())),
    ),
    { initialValue: readAngularNavigationId(this.location.getState()) },
  );

  private readonly analyticsService = inject(AnalyticsService);
  private readonly resetStateService = inject(ResetStateService);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly modalsService = inject(ModalsService);
  private readonly linkNavigation = inject(LinkNavigationService);
  private readonly translateService = inject(TranslateService);

  protected readonly currencyButtonCode = computed(() => {
    return (
      this.displayCurrency.selectedRate()?.code ??
      this.marketplaceSelector.selectMarketplace().currency ??
      ''
    );
  });

  protected getBackgroundStyles(): Record<string, string | IContentBuilderFieldColor | null> {
    const backgroundColor = this.backgroundColor();
    const backgroundImage = this.backgroundImage();
    return {
      'background-color': backgroundColor ? backgroundColor : null,
      'background-image': backgroundImage ? `url(${backgroundImage.url})` : null,
      'background-size': backgroundImage ? 'cover' : null,
    };
  }

  protected hasNavigableLink(link: string | LinkValue | undefined): boolean {
    return isLinkValueComplete(link);
  }

  protected onBackgroundClick(event: MouseEvent): void {
    const link = this.backgroundUrl();
    if (this.hasNavigableLink(link) && event.target === event.currentTarget) {
      this.linkNavigation.navigate(link);
    }
  }

  protected onLogoClick(event: MouseEvent): void {
    const link = this.logoUrl();
    if (this.hasNavigableLink(link) && event.target === event.currentTarget) {
      this.linkNavigation.navigate(link);
    }
  }

  protected goBack(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.previewState.isActive() || this.navigationId() <= 1) {
      return;
    }
    this.location.back();
  }

  protected redirectoToHome(event: Event): void {
    event.stopPropagation();
    if (this.homeNavigationPending) {
      return;
    }

    const cartShopId = kioskCartShopId(this.cartSelector.selectActive());
    const knownPath = kioskShopHomePath(
      resolveKioskHomeShop(
        this.shopSelector.selectActive(),
        this.shopSelector.shops,
        this.shopSelector.selectActiveId() ?? cartShopId,
      ),
    );
    if (knownPath) {
      void this.navigateHome(knownPath);
      return;
    }

    if (!cartShopId) {
      return;
    }

    this.homeNavigationPending = true;
    this.homeLoaderTimer = setTimeout(() => {
      this.showHomeLoader.set(true);
    }, KIOSK_HOME_LOADER_DELAY_MS);

    let navigating = false;
    this.shopsService
      .loadShopById$(cartShopId)
      .pipe(
        take(1),
        takeUntil(this._destroyerRef),
        finalize(() => {
          if (!navigating) {
            this.stopHomeLoader();
          }
        }),
      )
      .subscribe(shop => {
        const path = kioskShopHomePath(shop);
        if (!path) {
          return;
        }
        navigating = true;
        void this.navigateHome(path).finally(() => this.stopHomeLoader());
      });
  }

  private navigateHome(shopHomePath: string): Promise<boolean> {
    const translated = this.routerHelper.translateRoute(shopHomePath);
    const url = Array.isArray(translated) ? translated.join('/') : translated;
    return this.router.navigateByUrl(url.startsWith('/') ? url : `/${url}`);
  }

  private stopHomeLoader(): void {
    if (this.homeLoaderTimer !== undefined) {
      clearTimeout(this.homeLoaderTimer);
      this.homeLoaderTimer = undefined;
    }
    this.homeNavigationPending = false;
    this.showHomeLoader.set(false);
  }

  override ngOnDestroy(): void {
    this.stopHomeLoader();
    super.ngOnDestroy();
  }

  protected currencyButtonSymbol(): string {
    const code = this.currencyButtonCode();
    const symbol = currencyNarrowSymbol(code, this.translateService.currentLang || 'en');
    return symbol.toUpperCase() === code.toUpperCase() ? '' : symbol;
  }

  protected openCurrencyPicker(event: Event): void {
    event.stopPropagation();
    this.modalsService.open(KioskCurrencyPickerModalComponent.handle);
  }

  protected async leaveSession(): Promise<void> {
    this.isLeaving.set(true);
    await this.resetStateService.refreshUIAndResetLang();
  }
}
